import {
  compatWrapper,
  convertLegacyRouteRef,
} from '@backstage/core-compat-api';
import { getComponentData, RouteRef } from '@backstage/core-plugin-api';
import type { ComponentType } from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import {
  ArgocdDeploymentLifecycle,
  isArgocdConfigured,
} from '@backstage-community/plugin-argocd/legacy';
import {
  EntityAzurePipelinesContent,
  EntityAzurePullRequestsContent,
  isAzureDevOpsAvailable,
} from '@backstage-community/plugin-azure-devops';
import entityFeedbackPlugin from '@backstage-community/plugin-entity-feedback/alpha';
import pullRequestsBoardPlugin from '@backstage-community/plugin-github-pull-requests-board/alpha';
import { isPluginApplicableToEntity as isPagerDutyAvailable } from '@pagerduty/backstage-plugin';
import { componentVariants, hasGithubTeamSlug, onVariants } from './variants';

/**
 * The route ref a legacy routable extension (`createRoutableExtension`)
 * requires to be mounted. Such components throw "was not discovered in the
 * app element tree" unless the tab rendering them mounts that ref.
 */
function mountPointOf(component: ComponentType<any>) {
  const routeRef = getComponentData<RouteRef>(
    { type: component, props: {}, key: null },
    'core.mountPoint',
  );
  if (!routeRef) {
    throw new Error(
      `${component.displayName ?? component.name} is not a routable extension`,
    );
  }
  return convertLegacyRouteRef(routeRef);
}

/*
 * Entity cards and tabs the native plugins do not provide in the form the
 * portal uses (no native tab, or props the native extension cannot set).
 * Each lives in a module for the plugin it wraps.
 */

const pagerDutyModule = createFrontendModule({
  pluginId: 'pagerduty',
  extensions: [
    EntityContentBlueprint.make({
      name: 'pagerduty',
      params: {
        path: '/pagerduty',
        title: 'PagerDuty',
        filter: onVariants(componentVariants, isPagerDutyAvailable),
        loader: () =>
          import('@pagerduty/backstage-plugin').then(m =>
            compatWrapper(
              <m.EntityPagerDutyCard
                disableChangeEvents={false}
                readOnly={false}
                disableOnCall={false}
              />,
            ),
          ),
      },
    }),
  ],
});

const azureDevOpsModule = createFrontendModule({
  pluginId: 'azure-devops',
  extensions: [
    EntityContentBlueprint.make({
      name: 'readme',
      params: {
        path: '/readme',
        title: 'README',
        filter: onVariants(['website'], isAzureDevOpsAvailable),
        loader: () =>
          import('@backstage-community/plugin-azure-devops').then(m =>
            compatWrapper(<m.EntityAzureReadmeCard />),
          ),
      },
    }),
    EntityContentBlueprint.make({
      name: 'ci-cd',
      params: {
        path: '/ci-cd',
        title: 'CI/CD',
        routeRef: mountPointOf(EntityAzurePipelinesContent),
        filter: onVariants(['website']),
        loader: () =>
          import('./CiCdContent').then(m => compatWrapper(<m.CiCdContent />)),
      },
    }),
    // Replaces the native tab, which has no way to set `defaultLimit`.
    EntityContentBlueprint.make({
      name: 'pull-requests',
      params: {
        path: '/pull-requests',
        title: 'Pull Requests',
        routeRef: mountPointOf(EntityAzurePullRequestsContent),
        filter: onVariants(['website'], isAzureDevOpsAvailable),
        loader: () =>
          import('@backstage-community/plugin-azure-devops').then(m =>
            compatWrapper(<m.EntityAzurePullRequestsContent defaultLimit={25} />),
          ),
      },
    }),
  ],
});

const entityValidationModule = createFrontendModule({
  pluginId: 'entity-validation',
  extensions: [
    EntityContentBlueprint.make({
      name: 'validation',
      params: {
        path: '/validation',
        title: 'Validation',
        filter: onVariants(['service']),
        loader: () =>
          import('@backstage-community/plugin-entity-validation').then(m =>
            compatWrapper(<m.EntityValidationContent />),
          ),
      },
    }),
  ],
});

const entityFeedbackModule = createFrontendModule({
  pluginId: 'entity-feedback',
  extensions: [
    // Replaces the native card to keep the labelled button group.
    EntityCardBlueprint.make({
      name: 'ratings-buttons',
      params: {
        type: 'info',
        filter: onVariants(componentVariants),
        loader: () =>
          import('./RateThisEntityCard').then(m =>
            compatWrapper(<m.RateThisEntityCard />),
          ),
      },
    }),
    entityFeedbackPlugin
      .getExtension('entity-card:entity-feedback/ratings-table')
      .override({
        params: { type: 'info', filter: onVariants(['user', 'group']) },
      }),
    entityFeedbackPlugin
      .getExtension('entity-content:entity-feedback')
      .override({ params: { filter: onVariants(['service']) } }),
  ],
});

// The native extensions filter on kind only; these replacements (same ids)
// also require the `github.com/team-slug` annotation, as before. The alpha
// plugin is typed without `getExtension`, so they are re-made rather than
// overridden.
const pullRequestsBoardModule = createFrontendModule({
  pluginId: 'github-pull-requests-board',
  extensions: [
    EntityCardBlueprint.make({
      name: 'overview',
      params: {
        type: 'content',
        filter: onVariants(['group'], hasGithubTeamSlug),
        loader: () =>
          import('@backstage-community/plugin-github-pull-requests-board').then(
            m => compatWrapper(<m.EntityTeamPullRequestsCard />),
          ),
      },
    }),
    EntityContentBlueprint.make({
      name: 'entity',
      params: {
        path: '/pull-requests',
        title: 'Pull Requests',
        routeRef: pullRequestsBoardPlugin.routes.entityContent,
        filter: onVariants(['group'], hasGithubTeamSlug),
        loader: () =>
          import('@backstage-community/plugin-github-pull-requests-board').then(
            m => compatWrapper(<m.EntityTeamPullRequestsContent />),
          ),
      },
    }),
  ],
});

// Same id as the native tab, re-made because the plugin's default export is
// typed without `getExtension`.
const argocdModule = createFrontendModule({
  pluginId: 'backstage-community-argocd',
  extensions: [
    EntityContentBlueprint.make({
      name: 'deployment-lifecycle',
      params: {
        path: '/cd',
        title: 'CD',
        routeRef: mountPointOf(ArgocdDeploymentLifecycle),
        filter: onVariants(['service'], isArgocdConfigured),
        loader: () =>
          import('@backstage-community/plugin-argocd/legacy').then(m =>
            compatWrapper(<m.ArgocdDeploymentLifecycle />),
          ),
      },
    }),
  ],
});

export const pluginWrapperModules = [
  argocdModule,
  pullRequestsBoardModule,
  pagerDutyModule,
  azureDevOpsModule,
  entityValidationModule,
  entityFeedbackModule,
];
